// One cell of a scorecard grid (HOME-CAD-UX 2026-10-21; EOS / Ninety.io and KPI Beast's
// tracker): the period's value, tinted by whether it met its target. Never colour alone: the
// state is also text for screen readers, and "no data" is a muted dot, never a zero or a red.
// With `onEdit` the cell is a button (KPI Beast: click a cell to enter the number); while a
// value is being saved it waits at half opacity. `ScoreCellInput` replaces the cell in place.
// KPIS-MODE (founder-approved 2026-10-22): an editable cell is visibly editable — a small
// pencil in its top-right corner on hover and on keyboard focus (always on a coarse pointer),
// the inset focus ring, and the rest props (`onKeyDown`, `ref`, data attributes) on the button
// too, so a grid can type into it and walk it from the keyboard.
import { PencilIcon } from "lucide-react";
import * as React from "react";

import { cn } from "../cn";
import { FOCUS_RING_INSET } from "../components/focus-ring";
import { HOVER_EASE } from "../components/states";
import { SCORE_MARK_LABELS, type ScoreMarkState } from "./score-mark";

const TINT: Record<ScoreMarkState, string> = {
  hit: "bg-success-soft text-success-text",
  near: "bg-warning-soft text-warning-text",
  miss: "bg-danger-soft text-danger-text",
  none: "text-muted-foreground",
};

export type ScoreCellProps = Omit<
  React.ComponentProps<"span">,
  "children" | "onClick" | "onKeyDown" | "ref"
> & {
  ref?: React.Ref<HTMLElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  state: ScoreMarkState;
  /** The formatted value; `null` shows a muted dot. */
  value: React.ReactNode | null;
  /** The full reading for screen readers and the tooltip ("Hit: 12 (target ≥ 10)"). */
  label?: string;
  /** Click to enter or change the value (managers, native cadence, not a future period). */
  onEdit?: () => void;
  /** An entered value on its way to the API. */
  pending?: boolean;
};

export function ScoreCell({
  state,
  value,
  label,
  onEdit,
  pending = false,
  className,
  ref,
  ...props
}: ScoreCellProps) {
  const text = label ?? SCORE_MARK_LABELS[state];
  // A full `label` carries the number itself; otherwise the value reads, then the state.
  const body = (
    <>
      <span aria-hidden={label ? true : undefined} className="tabular-nums">
        {value === null ? <span className="text-faint">–</span> : value}
      </span>
      <span className="sr-only">{label ? text : ` ${text}`}</span>
    </>
  );
  const base = cn(
    "flex h-9 w-full min-w-0 items-center justify-end px-2 text-sm tabular-nums",
    TINT[state],
    pending && "opacity-50",
    className,
  );
  if (onEdit)
    return (
      <button
        type="button"
        data-slot="score-cell"
        data-state={state}
        data-editable=""
        title={text}
        ref={ref as React.Ref<HTMLButtonElement>}
        {...(props as React.ComponentProps<"button">)}
        className={cn(
          base,
          "group/score-cell relative cursor-text rounded-xs hover:outline hover:outline-1 hover:outline-border-strong",
          HOVER_EASE,
          FOCUS_RING_INSET,
        )}
        onClick={onEdit}
      >
        {body}
        <PencilIcon
          aria-hidden="true"
          data-slot="score-cell-pencil"
          className="pointer-events-none absolute top-0.5 right-0.5 size-2.5 text-muted-foreground opacity-0 transition-opacity group-hover/score-cell:opacity-100 group-focus-visible/score-cell:opacity-100 pointer-coarse:opacity-100"
        />
      </button>
    );
  return (
    <span
      data-slot="score-cell"
      data-state={state}
      title={text}
      className={base}
      ref={ref as React.Ref<HTMLSpanElement>}
      {...props}
    >
      {body}
    </span>
  );
}

export type ScoreCellInputProps = Omit<React.ComponentProps<"input">, "type"> & {
  /** Names the cell for screen readers ("Placements, week 42"). */
  "aria-label": string;
};

/** The in-place editor of a cell: a bare 36 px input, right-aligned, on the accent outline. */
export const ScoreCellInput = React.forwardRef<HTMLInputElement, ScoreCellInputProps>(
  function ScoreCellInput({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        data-slot="score-cell-input"
        className={cn(
          "block h-9 w-full min-w-0 rounded-xs bg-card px-2 text-right text-sm tabular-nums text-foreground outline-2 outline-solid outline-ring -outline-offset-2 placeholder:text-muted-foreground",
          className,
        )}
        {...props}
      />
    );
  },
);
